import ProjectsTable from './components/ProjectsTable/ProjectsTable';
import { ProjectsTableRow, Meta } from './components/ProjectsTable/helpers';
import { useProjectsList } from './useProjectsList';
import styles from './ProjectsList.module.css';
import { useMemo, useState } from 'react';
import CreateProjectModal from './components/CreateProjectModal/CreateProjectModal';
import { SearchInput, assets } from '@round/whitelabel-ui';
import { creatorbase, musicQueries } from '@round/api';
import { useQuery } from '@tanstack/react-query';
import { useFeatureFlags } from 'utility/featureFlags/FeatureFlagsContext';
import useUrlState from 'Hooks/useUrlState';
import { Checkbox, ScrollView } from '@round/ui-kit';
import { useCurrentCreatorbaseUser } from 'Modules/Auth/hooks/useCurrentCreatorbaseUser';
import { Button } from '@round/whitelabel-ui';
import { PageTitle } from '@round/whitelabel-ui';
import SongDataModal from 'Modules/Plans/Songs/SongDataModal/SongDataModal';
import { buildFetchOptions, SongOption, useSongMultiSelect } from 'Modules/Plans/Songs/hooks/useSongSelect';
import { Select } from '@round/whitelabel-ui';
import { FormatOptionLabelMeta, StylesConfig, ValueType } from 'react-select';
import SongBadge from 'Modules/Plans/Songs/components/SongBadge/SongBadge';
import DeleteProjectModal from './components/DeleteProjectModal/DeleteProjectModal';
import useClientSelect from 'Modules/Plans/Clients/hooks/useClientSelect';
import { isNumber } from 'utility/utility';
import useEffectWithPreviousDeps from 'Hooks/useEffectWithPreviousDeps';
import { OwnedBySelect } from '@round/creatorbase-helpers/components';
import usePartnershipSelect from 'Modules/Plans/Partnerships/hooks/usePartnershipSelect';

const { Plus: PlusIcon } = assets;

type UrlState = Partial<
    Pick<creatorbase.GetProjectsParams, 'search'> & {
        my_projects: string;
        song: string;
        client_song_id: string;
        genres: string;
        owned_by: string;
    }
>;

const ProjectsList = () => {
    const { creatorbaseUserId } = useCurrentCreatorbaseUser();
    const [urlState, setUrlState] = useUrlState<UrlState>();

    const isMyProjectsFilterActive = urlState.my_projects === 'true';
    const myProjectsFilterValue =
        isMyProjectsFilterActive && creatorbaseUserId ? creatorbaseUserId.toString() : undefined;

    const [isCreateProjectModalOpen, setIsCreateProjectModalOpen] = useState(false);
    const [isDeleteProjectModalOpen, setIsDeleteProjectModalOpen] = useState(false);

    const [projectRowToDelete, setProjectRowToDelete] = useState<ProjectsTableRow | null>(null);

    const onClickDeleteProject = (project: ProjectsTableRow) => {
        setProjectRowToDelete(project);
        setIsDeleteProjectModalOpen(true);
    };

    const { selectedClientId } = useClientSelect();

    const isProjectSharingEnabled = useFeatureFlags().isEnabled('project_sharing');
    const ownedByParam = Number(urlState.owned_by);
    const ownedByFilter = Number.isInteger(ownedByParam) && ownedByParam > 0 ? ownedByParam : undefined;

    const { getProps: getPartnershipSelectProps } = usePartnershipSelect({
        initOn: 'mount',
        params: { client_id: selectedClientId ?? undefined, state: 'active,ended' },
        initialValueId: ownedByFilter,
        enabled: isProjectSharingEnabled && isNumber(selectedClientId),
    });

    const isGenresEnabled = useFeatureFlags().isEnabled('rou_1766_project_genres');
    const { data: genreOptions } = useQuery({ ...musicQueries.genres.options.listAll(), enabled: isGenresEnabled });
    const selectedGenres = (genreOptions ?? []).filter((option) => urlState.genres?.split(',').includes(option.value));

    const {
        props: songSelectProps,
        resetValue: resetSongFilterValue,
        resetOptions: resetSongFilterOptions,
    } = useSongMultiSelect({
        initOn: 'menuOpen',
        initialValueId: urlState.client_song_id?.split(',').map((id) => Number(id)),
        fetchOptions: useMemo(() => buildFetchOptions({ client_id: selectedClientId }), [selectedClientId]),
    });

    const {
        rows,
        meta: projectsListMeta,
        hasNextPage,
        isFetchingNextPage,
        fetchNextPage,
    } = useProjectsList(
        {
            search: urlState.search,
            users_assigned_to_campaigns: myProjectsFilterValue,
            client_song_id: urlState.client_song_id,
            genres: isGenresEnabled ? urlState.genres : undefined,
            owned_by: isProjectSharingEnabled ? ownedByFilter ?? 'me' : undefined,
            client_id: isNumber(selectedClientId) ? selectedClientId : undefined,
        },
        { enabled: isNumber(selectedClientId) }
    );

    useEffectWithPreviousDeps(
        (prevDeps) => {
            const [, , prevClientId] = prevDeps ?? [];
            // otherwise it runs on every url state change
            if (prevClientId && prevClientId !== selectedClientId) {
                resetSongFilterValue();
                resetSongFilterOptions();
                setUrlState({ client_song_id: undefined, owned_by: undefined });
            }
        },
        [resetSongFilterOptions, resetSongFilterValue, selectedClientId, setUrlState]
    );

    const songSelectStyles: StylesConfig = {
        container: (base) => ({
            ...base,
            minWidth: '20rem',
            maxWidth: '30rem',
        }),
        valueContainer: (base) => ({
            ...base,
            flexWrap: 'nowrap',
            overflowX: 'auto',
        }),
        multiValue: (base) => ({
            ...base,
            minWidth: 'min-content',
        }),
        menu: (base) => ({
            ...base,
            width: '100%',
            minWidth: '20rem',
            '@media only screen and (min-width: 768px)': {
                width: 'max-content',
            },
        }),
    };

    const genreSelectStyles: StylesConfig = {
        container: (base) => ({
            ...base,
            minWidth: '14rem',
            maxWidth: '22rem',
        }),
        valueContainer: (base) => ({
            ...base,
            flexWrap: 'nowrap',
            overflowX: 'auto',
        }),
        multiValue: (base) => ({
            ...base,
            minWidth: 'min-content',
        }),
        menu: (base) => ({
            ...base,
            width: 'max-content',
            minWidth: '14rem',
        }),
    };

    const meta: Meta = {
        ...projectsListMeta,
        onSongChartClick: (songId) => setUrlState({ song: songId.toString() }),
        onDeleteProject: (project) => onClickDeleteProject(project),
    };

    return (
        <>
            <div className={styles.container}>
                <header className={styles.header}>
                    <PageTitle>Projects</PageTitle>

                    <Button
                        appearance="primary"
                        onClick={() => setIsCreateProjectModalOpen(true)}
                        aria-label="Create Project"
                    >
                        <PlusIcon />
                        Project
                    </Button>
                </header>

                <menu className={styles.menuBar}>
                    <SearchInput
                        value={urlState.search ?? ''}
                        onChange={(val) => setUrlState({ search: val })}
                        className={styles.search}
                    />

                    {isProjectSharingEnabled && (
                        <OwnedBySelect
                            {...getPartnershipSelectProps({
                                onChange: (option) => setUrlState({ owned_by: option?.value.toString() }),
                            })}
                        />
                    )}

                    <Select
                        {...songSelectProps}
                        placeholder="Filter by song..."
                        styles={songSelectStyles}
                        onChange={(value, option) => {
                            songSelectProps.onChange?.(value, option);
                            setUrlState({ client_song_id: value?.map((obj) => obj.value).join(',') });
                        }}
                        value={songSelectProps.value as ValueType<SongOption, true>}
                        formatOptionLabel={(option: SongOption, meta: FormatOptionLabelMeta<SongOption, true>) =>
                            meta.context === 'menu' ? (
                                <SongBadge
                                    title={option.label}
                                    artistName={option.artistName}
                                    imageUrl={option.image}
                                />
                            ) : (
                                option.label
                            )
                        }
                        isMulti
                    />

                    {isGenresEnabled && (
                        <Select
                            isMulti
                            aria-label="Filter by genre"
                            placeholder="Filter by genre..."
                            styles={genreSelectStyles}
                            options={genreOptions ?? []}
                            value={selectedGenres}
                            onChange={(value) =>
                                setUrlState({ genres: value?.map((option) => option.value).join(',') || undefined })
                            }
                        />
                    )}

                    <div className={styles.myProjectsFilter}>
                        <Checkbox
                            value={isMyProjectsFilterActive}
                            onChange={() => setUrlState({ my_projects: isMyProjectsFilterActive ? undefined : 'true' })}
                        />
                        <label
                            //checkbox component isn't an input, so id and htmlFor can't currently be used
                            onClick={() => setUrlState({ my_projects: isMyProjectsFilterActive ? undefined : 'true' })}
                        >
                            Only my Projects
                        </label>
                    </div>
                </menu>

                <ScrollView className={styles.tableContainer}>
                    <ProjectsTable
                        meta={meta}
                        data={rows}
                        onEndReached={() => {
                            if (hasNextPage && !isFetchingNextPage && isNumber(selectedClientId)) {
                                void fetchNextPage();
                            }
                        }}
                    />
                </ScrollView>
            </div>

            <CreateProjectModal isOpen={isCreateProjectModalOpen} onClose={() => setIsCreateProjectModalOpen(false)} />
            <DeleteProjectModal
                project={projectRowToDelete}
                isOpen={isDeleteProjectModalOpen}
                onClose={() => {
                    setIsDeleteProjectModalOpen(false);
                    setProjectRowToDelete(null);
                }}
            />

            <SongDataModal
                key={urlState.song}
                songId={urlState.song ? Number(urlState.song) : undefined}
                isOpen={!!urlState.song}
                onClose={() => setUrlState({ song: undefined })}
            />
        </>
    );
};

export default ProjectsList;
